import React from "react";
import { Link, Box } from "@mui/material";
import { Button } from "@mui/material";

import SbuxTranslate from "../../SbuxTranslate";
import SbuxContentCopy from "../../SbuxContentCopy";
import defaultConfig from "../config/default.json";
import languageConfig from "../config/language.json";
import { useAppDispatch, useAppSelector } from "../../../store/types";
import {
  createOrUpdateSettings,
  createOrUpdateLocaleSettings,
  getReOrderedTablist,
  getUpdatedTimezone,
} from "../../../services/settings";
import { USER_DEFAULT_TIMEZONE } from "../../../constants";
import styles from "../styles";
import useCss from "../../../hooks/useCss";
import {
  getUserConfigSelector,
  getLocaleSelector,
  getReOrderedTablistSelector,
  getSelectedTimezoneSelector,
} from "../../../selectors/settingsSelector";
import { getInstanceIdSelector } from "../../../selectors/userInstanceSelector";

interface Props {
  handleModal: () => void;
}

const FooterList = ({ handleModal }: Props) => {
  const classes = useCss(styles);
  const dispatch = useAppDispatch();

  const config = useAppSelector(getUserConfigSelector);
  const locale = useAppSelector(getLocaleSelector);
  const reOrderedTablist = useAppSelector(getReOrderedTablistSelector);
  const selectedTimezone = useAppSelector(getSelectedTimezoneSelector);
  const instanceId = useAppSelector(getInstanceIdSelector);

  const handlSaveClick = async () => {
    const userConfig = config as {
      tabOrder?: unknown;
      timezone?: string;
      [key: string]: unknown;
    } | null;
    let payload: Record<string, unknown>;
    if (userConfig?.tabOrder) {
      payload = {
        config: {
          ...userConfig,
          tabOrder: reOrderedTablist,
          languageCode: locale,
          timezone: selectedTimezone
            ? selectedTimezone
            : defaultConfig.config.timezone,
        },
      };
    } else {
      payload = {
        config: {
          tabOrder: reOrderedTablist,
          languageCode: locale,
          timezone: selectedTimezone
            ? selectedTimezone
            : defaultConfig.config.timezone,
        },
      };
    }
    const timezone = selectedTimezone
      ? selectedTimezone
      : defaultConfig.config.timezone;
    handleModal();
    await dispatch(getUpdatedTimezone(timezone));
    await dispatch(createOrUpdateSettings(payload));
    await dispatch(createOrUpdateLocaleSettings(languageConfig.translations));
  };

  const handlCancelClick = async () => {
    handleModal();
    await dispatch(getReOrderedTablist("cancel"));
    const userConfig = config as { timezone?: string } | null;
    await dispatch(
      getUpdatedTimezone(userConfig?.timezone || defaultConfig.config.timezone),
    );
  };

  const handleLinkClick = async () => {
    handleModal();
    const resetToDefaults = {
      languageCode: "en",
      default: languageConfig.translations,
    };
    await dispatch(getUpdatedTimezone(USER_DEFAULT_TIMEZONE));
    await dispatch(createOrUpdateSettings(defaultConfig));
    await dispatch(createOrUpdateLocaleSettings(resetToDefaults));
  };

  return (
    <Box className={classes.footerContainer}>
      <Box className={classes.actionButtons}>
        <Button variant="contained" size="medium" onClick={handlSaveClick}>
          <SbuxTranslate>{"Save"}</SbuxTranslate>
        </Button>

        <Button variant="outlined" size="medium" onClick={handlCancelClick}>
          <SbuxTranslate>{"Cancel"}</SbuxTranslate>
        </Button>
      </Box>
      <Box sx={{ fontSize: 18, flex: 1 }}>
        <Link component="button" onClick={handleLinkClick}>
          <SbuxTranslate>{"Click to reset to defaults"}</SbuxTranslate>
        </Link>
      </Box>
      {instanceId && (
        <Box sx={{ fontSize: 14, marginTop: 2, alignItems: "center", gap: 1 }}>
          <span>Instance ID: {instanceId}</span>
          <SbuxContentCopy content={instanceId} />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(FooterList);
